import { useState, useEffect } from 'react';
import { Box, Typography, Container, Stack, Fade, useTheme, Paper, Divider } from '@mui/material';
import { Logo } from '@carcareers/components';
import { SplashScreen1 } from '@carcareers/assets';

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export const SplashScreen = () => {
  const theme = useTheme();
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);

    const calculateTimeLeft = () => {
      // Create target date for July 7th, 2025
      const targetDate = new Date(2025, 6, 7, 0, 0, 0, 0).getTime(); // Month is 0-indexed, so 6 = July
      const now = new Date().getTime();
      const difference = targetDate - now;

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        setTimeLeft({ days, hours, minutes, seconds });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, []);

  const TimeBlock = ({ value, label }: { value: number; label: string }) => (
    <Box
      sx={{
        px: { xs: 2, sm: 3 },
        py: { xs: 1.5, sm: 2 },
        minWidth: { xs: 80, sm: 100 },
        textAlign: 'center',
        borderRadius: 3,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        },
      }}
    >
      <Typography
        variant="h3"
        component="div"
        sx={{
          fontWeight: 'bold',
          color: '#FFFFFF',
          fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
          lineHeight: 1,
          textShadow: '0 2px 4px rgba(0,0,0,0.3)',
        }}
      >
        {value.toString().padStart(2, '0')}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'rgba(255, 255, 255, 0.9)',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 1.5,
          fontSize: { xs: '0.7rem', sm: '0.8rem' },
          mt: 0.5,
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: '100vw',
          height: '100vh',
          backgroundImage: `url(${SplashScreen1})`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'bottom right',
          opacity: 0.2,
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Stack spacing={4} alignItems="center" justifyContent="center" sx={{ minHeight: '100vh', py: 4 }}>
          <Fade in={mounted} timeout={1000}>
            <Box>
              <Logo sx={{ width: { xs: 200, sm: 250, md: 300 } }} />
            </Box>
          </Fade>

          <Fade in={mounted} timeout={1500}>
            <Stack spacing={5} alignItems="center" textAlign="center">
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.primary.main,
                  width: '75%',
                  fontSize: { xs: '1rem', sm: '1rem', md: '2rem' },
                  textAlign: 'center',
                  lineHeight: 1.2,
                }}
              >
                Hiring for Anybody, Everybody, Anything, Everything Automotive Related
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  fontWeight: 500,
                  textAlign: 'center',
                  maxWidth: '600px',
                }}
              >
                We're building the future of automotive careers!
              </Typography>
            </Stack>
          </Fade>

          <Fade in={mounted} timeout={2500}>
            <Stack spacing={5} alignItems="center" textAlign="center">
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: { xs: '1.1rem', sm: '1.3rem' },
                  fontWeight: 500,
                  maxWidth: '600px',
                }}
              >
                Get ready for a completely redesigned experience that will revolutionize how Job Seekers find Automotive
                Careers and how Industry Employers fill their open positions!
              </Typography>
            </Stack>
          </Fade>

          {/* Countdown Timer */}
          <Fade in={mounted} timeout={3000}>
            <Stack spacing={3} alignItems="center">
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.secondary.main,
                  textAlign: 'center',
                  fontSize: { xs: '1rem', sm: '1.5rem' },
                }}
              >
                We're Back July 7th, 2025!
              </Typography>

              <Stack
                direction="row"
                spacing={{ xs: 1, sm: 2 }}
                alignItems="center"
                flexWrap="wrap"
                justifyContent="center"
                sx={{
                  p: 5,
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.light})`,
                  borderRadius: 3,
                  boxShadow: `0 8px 32px ${theme.palette.primary.main}30`,
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <TimeBlock value={timeLeft.days} label="Days" />
                <TimeBlock value={timeLeft.hours} label="Hours" />
                <TimeBlock value={timeLeft.minutes} label="Minutes" />
                <TimeBlock value={timeLeft.seconds} label="Seconds" />
              </Stack>
            </Stack>
          </Fade>

          <Fade in={mounted} timeout={3500}>
            <Stack spacing={1} alignItems="center" textAlign="center">
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                }}
              >
                Mark your calendar and be ready for the most exciting launch in automotive careers!
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.disabled,
                  fontSize: '0.8rem',
                  fontStyle: 'italic',
                }}
              >
                The wait will be worth it. Trust us.
              </Typography>
            </Stack>
          </Fade>
        </Stack>
      </Container>
    </Box>
  );
};

export default SplashScreen;
